import { countryFlag } from './utils/countries';
import { initRiderSearch } from './rider-search';
import { titleCase } from './utils/text';
import { renderCountryBar, CHART_KEY as KEY_COUNTRY }             from './charts/countryBar';
import { renderYearsSkating as drawYearsSkating }                  from './charts/yearsSkating';
import { C_TEAL } from './utils/palette';
import { renderYoyBar, CHART_KEY as KEY_YOY }                     from './charts/yoyBar';
import { renderTrajectory, CHART_KEY as KEY_TRAJECTORY }          from './charts/trajectory';
import { renderActivityHeatmap, CHART_KEY as KEY_ACTIVITY }       from './charts/activityHeatmap';
import { renderRoundDonut, CHART_KEY as KEY_ROUND }               from './charts/roundDonut';
import { renderPointsDistribution, CHART_KEY as KEY_POINTS }      from './charts/pointsDistribution';
import { renderPredictionBar, CHART_KEY as KEY_PREDICTION }       from './charts/predictionBar';
import { renderMomentumHeatmap, CHART_KEY as KEY_MOMENTUM }       from './charts/momentumHeatmap';
import { renderAgeScatter, CHART_KEY as KEY_SCATTER }             from './charts/ageScatter';
import { BottomSheet } from './bottom-sheet.js';

const KEY_AGE   = ['X = age · Y = rider count · distribution across all ranked riders'];
const KEY_YEARS = ['X = years skating · Y = rider count · distribution across all ranked riders'];

function fillKey(el, lines) {
    if (!el || !lines) return;
    el.innerHTML = lines.map(l => `<span>${l}</span>`).join('');
}

document.addEventListener('alpine:init', () => {
    Alpine.data('ranking', (sportId) => ({
        titleCase,
        ranking: {},
        loading: true,
        activeTab: null,
        countryLegend: [],
        trajClipPct: 100,
        pointsClipPct: 100,
        scatterClipPct: 100,

        formatPts(n) {
            return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        ordinal(n) {
            const s = ['th','st','nd','rd'], v = n % 100;
            return n + (s[(v-20)%10] ?? s[v] ?? s[0]);
        },

        podiumClass(pos) {
            if (pos === 1) return 'text-yellow-500 font-bold';
            if (pos === 2) return 'text-slate-400 font-semibold';
            if (pos === 3) return 'text-orange-400 font-semibold';
            return '';
        },

        flag(country) {
            return countryFlag(country);
        },

        tooltipHtml(allRounds) {
            if (!allRounds || !allRounds.length) return '';
            return allRounds.map(e => {
                const bonusColor = e.position === 1 ? 'text-yellow-500' : e.position === 2 ? 'text-slate-400' : 'text-orange-400';
                const bonusHtml  = e.bonus ? `<span class='${bonusColor} text-[9px]'>+${e.bonus}</span>` : '';
                let breakdown = '';
                if (e.scored && e.rw != null && e.yw != null) {
                    breakdown = `<span class='text-neutral-500 text-[9px]'>10k×1/${e.position}×${e.rw}×${e.yw}</span>`;
                }
                return `<div class='flex gap-3 justify-between items-center${e.scored ? '' : ' opacity-40'}'>` +
                    `<span class='text-neutral-400'>${e.round}</span>` +
                    `<span>${this.ordinal(e.position)}</span>` +
                    `${breakdown}` +
                    `${bonusHtml}` +
                    `<span class='text-neutral-300'>${e.scored ? this.formatPts(e.pts) : '—'}</span>` +
                    `</div>`;
            }).join('');
        },

        instagramUrl(handle) {
            if (!handle) return null;
            if (handle.startsWith('http')) return handle.split('?')[0];
            const user = handle.replace(/^@/, '').trim();
            return user ? 'https://www.instagram.com/' + user : null;
        },

        switchTab(category) {
            this.activeTab = category;
            this.trajClipPct = 100;
            this.$nextTick(() => {
                this.renderAll(category);
                this.updatePeekStats();
            });
        },

        ageDistributionData(category) {
            const now    = new Date();
            const counts = {};
            this.getRiders(category).forEach(r => {
                const bd = r.data?.birthdate;
                if (!bd) return;
                const age = Math.floor((now - new Date(bd)) / (365.25 * 24 * 3600 * 1000));
                if (isNaN(age) || age < 5 || age > 80) return;
                counts[age] = (counts[age] ?? 0) + 1;
            });
            const labels = Object.keys(counts).map(Number).sort((a, b) => a - b);
            return { labels, data: labels.map(l => counts[l]) };
        },

        yearsSkatingData(category) {
            const counts = {};
            this.getRiders(category).forEach(r => {
                const val = parseInt(r.data?.years_skating);
                if (isNaN(val)) return;
                counts[val] = (counts[val] ?? 0) + 1;
            });
            const labels = Object.keys(counts).map(Number).sort((a, b) => a - b);
            return { labels, data: labels.map(l => counts[l]) };
        },

        getRiders(category) {
            const riders = this.ranking[category] ?? [];
            if (category === 'WCS Street Men') return riders.slice(0, Math.ceil(riders.length * 0.6));
            if (['WCS Street Women', 'WCS Miniramp'].includes(category)) return riders.slice(0, Math.ceil(riders.length * 0.8));
            return riders;
        },

        pointsDistributionData(category) {
            return this.getRiders(category)
                .filter(r => r.position != null && r.points != null)
                .map(r => ({ position: r.position, points: r.points }));
        },

        ageSkatingData(category) {
            const riders = this.getRiders(category);
            const n      = riders.length;
            const now    = new Date();
            const points = [];
            riders.forEach(r => {
                const bd = r.data?.birthdate;
                const ys = r.data?.years_skating;
                if (!bd || ys === null || ys === undefined || ys === '') return;
                const age   = Math.floor((now - new Date(bd)) / (365.25 * 24 * 3600 * 1000));
                const years = parseInt(ys);
                if (isNaN(age) || isNaN(years) || age < 5 || age > 80 || years > 60) return;
                const tier = r.position <= Math.ceil(n * 0.25) ? 'Semi'
                           : r.position <= Math.ceil(n * 0.50) ? 'Quali'
                           : 'Not Ranked';
                points.push({ age, years, group: tier, name: r.rider });
            });
            return points;
        },

        renderCountryBar(category) {
            this.countryLegend = renderCountryBar(this.$refs.countryBarChart, this.$refs.countryTooltip, this.getRiders(category));
            fillKey(this.$refs.countryKey, KEY_COUNTRY);
        },

        renderYearsSkating(category) {
            drawYearsSkating(this.$refs.yearsSkatingChart, this.yearsSkatingData(category));
            fillKey(this.$refs.yearsSkatingKey, KEY_YEARS);
        },

        renderAgeDistribution(category) {
            drawYearsSkating(this.$refs.ageDistributionChart, this.ageDistributionData(category), C_TEAL);
            fillKey(this.$refs.ageDistributionKey, KEY_AGE);
        },

        renderYoyBar(category) {
            renderYoyBar(this.$refs.yoyBarChart, this.$refs.yoyTooltip, this.getRiders(category));
            fillKey(this.$refs.yoyKey, KEY_YOY);
        },

        renderActivityHeatmap(category) {
            renderActivityHeatmap(this.$refs.activityHeatmapChart, this.$refs.activityTooltip, this.getRiders(category));
            fillKey(this.$refs.activityKey, KEY_ACTIVITY);
        },

        renderRoundDonut(category) {
            renderRoundDonut(this.$refs.roundDonutChart, this.getRiders(category));
            fillKey(this.$refs.roundKey, KEY_ROUND);
        },

        renderPredictionBar(category) {
            renderPredictionBar(this.$refs.predictionChart, this.$refs.predictionTooltip, this.getRiders(category));
            fillKey(this.$refs.predictionKey, KEY_PREDICTION);
        },

        renderMomentumHeatmap(category) {
            renderMomentumHeatmap(this.$refs.momentumChart, this.$refs.momentumTooltip, this.getRiders(category));
            fillKey(this.$refs.momentumKey, KEY_MOMENTUM);
        },

        renderTrajectory(category) {
            renderTrajectory(this.$refs.trajectoryChart, this.$refs.trajectoryTooltip, this.getRiders(category), this.trajClipPct);
            fillKey(this.$refs.trajectoryKey, KEY_TRAJECTORY);
        },

        renderPointsChart(category) {
            renderPointsDistribution(this.$refs.pointsChart, this.pointsDistributionData(category), this.pointsClipPct, this.$refs.pointsTooltip);
            fillKey(this.$refs.pointsKey, KEY_POINTS);
        },

        renderScatterChart(category) {
            renderAgeScatter(this.$refs.scatterChart, this.ageSkatingData(category), g => {
                if (g === 'Semi')       return 'semi';
                if (g === 'Quali')      return 'quali';
                if (g === 'Not Ranked') return 'not-ranked';
                return null;
            }, this.scatterClipPct, this.$refs.scatterTooltip);
            fillKey(this.$refs.scatterKey, KEY_SCATTER);
        },

        renderAll(category) {
            const riders = this.getRiders(category);
            this.countryLegend = renderCountryBar(this.$refs.countryBarChart, this.$refs.countryTooltip, riders);
            fillKey(this.$refs.countryKey, KEY_COUNTRY);
            drawYearsSkating(this.$refs.yearsSkatingChart, this.yearsSkatingData(category));
            fillKey(this.$refs.yearsSkatingKey, KEY_YEARS);
            drawYearsSkating(this.$refs.ageDistributionChart, this.ageDistributionData(category), C_TEAL);
            fillKey(this.$refs.ageDistributionKey, KEY_AGE);
            renderPointsDistribution(this.$refs.pointsChart, this.pointsDistributionData(category), this.pointsClipPct, this.$refs.pointsTooltip);
            fillKey(this.$refs.pointsKey, KEY_POINTS);
            renderAgeScatter(this.$refs.scatterChart, this.ageSkatingData(category), g => {
                if (g === 'Semi')       return 'semi';
                if (g === 'Quali')      return 'quali';
                if (g === 'Not Ranked') return 'not-ranked';
                return null;
            }, this.scatterClipPct, this.$refs.scatterTooltip);
            fillKey(this.$refs.scatterKey, KEY_SCATTER);
            renderYoyBar(this.$refs.yoyBarChart, this.$refs.yoyTooltip, riders);
            fillKey(this.$refs.yoyKey, KEY_YOY);
            renderTrajectory(this.$refs.trajectoryChart, this.$refs.trajectoryTooltip, riders, this.trajClipPct);
            fillKey(this.$refs.trajectoryKey, KEY_TRAJECTORY);
            renderActivityHeatmap(this.$refs.activityHeatmapChart, this.$refs.activityTooltip, riders);
            fillKey(this.$refs.activityKey, KEY_ACTIVITY);
            renderRoundDonut(this.$refs.roundDonutChart, riders);
            fillKey(this.$refs.roundKey, KEY_ROUND);
            renderPredictionBar(this.$refs.predictionChart, this.$refs.predictionTooltip, riders);
            fillKey(this.$refs.predictionKey, KEY_PREDICTION);
            renderMomentumHeatmap(this.$refs.momentumChart, this.$refs.momentumTooltip, riders);
            fillKey(this.$refs.momentumKey, KEY_MOMENTUM);
        },

        updatePeekStats() {
            const el = document.getElementById('stats-drawer-peek');
            if (!el || !this.activeTab) return;
            const riders    = this.getRiders(this.activeTab);
            const total     = riders.length;
            const countries = new Set(riders.map(r => r.data?.country).filter(Boolean)).size;
            el.textContent  = `${total} riders · ${countries} countries`;
        },

        async init() {
            const res = await fetch(`/api/public/sports/${sportId}/ranking`);
            this.ranking = await res.json();
            this.activeTab = Object.keys(this.ranking)[0] ?? null;
            this.loading = false;
            await this.$nextTick();
            this.renderAll(this.activeTab);

            initRiderSearch(this, () =>
                Object.entries(this.ranking).flatMap(([cat, riders]) =>
                    riders.slice(0, Math.ceil(riders.length * 0.8)).map(r => ({
                        category: cat,
                        name: r.rider ?? '',
                        id:   `rider-${cat.replace(/ /g, '-')}-${r.position}`,
                    }))
                )
            );

            const raw = window.location.hash.slice(1);
            if (raw.includes('|')) {
                const pipeIdx = raw.indexOf('|');
                const cat = decodeURIComponent(raw.slice(0, pipeIdx));
                const pos = raw.slice(pipeIdx + 1);
                if (cat && pos && this.ranking[cat]) {
                    if (cat !== this.activeTab) this.switchTab(cat);
                    await this.$nextTick();
                    const el = document.getElementById('rider-' + cat.replace(/ /g, '-') + '-' + pos);
                    if (el) {
                        const top = el.getBoundingClientRect().top + window.pageYOffset - window.innerHeight / 2;
                        window.scrollTo({ top, behavior: 'smooth' });
                        el.style.background = 'rgba(234,179,8,0.12)';
                        setTimeout(() => el.style.background = '', 2000);
                    }
                }
            }

            // Bottom sheet — mobile only, created/destroyed on breakpoint changes
            const setupSheet = () => {
                if (window.innerWidth < 1024) {
                    if (!this._sheet) {
                        this._sheet = new BottomSheet(document.getElementById('stats-drawer'), {
                            snapPoints: [0.2, 0.5, 0.92],
                            getScrollEl: () => document.getElementById('stats-drawer-body'),
                            onSnap: () => this.updatePeekStats(),
                        });
                        setTimeout(() => this.updatePeekStats(), 0);
                    }
                } else if (this._sheet) {
                    this._sheet.destroy();
                    this._sheet = null;
                }
            };
            setupSheet();
            window.addEventListener('resize', setupSheet);
        },
    }));
});
